import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  LogIn, UserPlus, MapPin, MessageSquare, Star,
  Wrench, Clock, Camera, Zap, Lock,
} from "lucide-react";
import { DirectionalArrow } from "@/components/DirectionalArrow";
import HirafiIcon from "@/components/HirafiIcon";
import { useAnnonces } from "@/hooks/useAnnonces";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useState } from "react";
import heroImage from "@/assets/hero-artisan.jpg";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";

const EspaceArtisan = () => {
  const { t, locale } = useLanguage("common");

  useSEO({
    title: "Devenir artisan sur Hirafi — Accédez à des missions locales",
    description: locale === "ar"
      ? "انضم إلى حرافي مجانًا وتمكّن من الوصول إلى طلبات الأفراد في جميع ولايات الجزائر. 0٪ عمولة، التسجيل في 5 دقائق."
      : "Rejoignez Hirafi gratuitement et accédez à des demandes de particuliers dans toutes les wilayas d'Algérie. 0% de commission, inscription en 5 minutes.",
    canonical: "/artisan",
    locale,
  });
  const lp = useLocalizedPath();
  const { user } = useAuth();
  const { data: annonces = [], isLoading } = useAnnonces();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const valueProps = [
    { icon: MapPin, title: t("espace_artisan.vp_local_title"), description: t("espace_artisan.vp_local_desc") },
    { icon: MessageSquare, title: t("espace_artisan.vp_msg_title"), description: t("espace_artisan.vp_msg_desc") },
    { icon: Star, title: t("espace_artisan.vp_visibility_title"), description: t("espace_artisan.vp_visibility_desc") },
  ];

  const dateLocale = locale === "ar" ? "ar-DZ" : "fr-FR";

  const getBudgetLabel = (annonce: typeof annonces[0]) => {
    if (annonce.budget_min && annonce.budget_max) {
      return t("espace_artisan.budget_range", {
        min: annonce.budget_min.toLocaleString(),
        max: annonce.budget_max.toLocaleString(),
      });
    }
    if (annonce.budget_min) {
      return t("espace_artisan.budget_from", { amount: annonce.budget_min.toLocaleString() });
    }
    if (annonce.budget_max) {
      return t("espace_artisan.budget_to", { amount: annonce.budget_max!.toLocaleString() });
    }
    return null;
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <nav className="sticky top-0 z-50 border-b bg-card/80 backdrop-blur-md">
        <div className="container flex h-16 items-center justify-between">
          <Link to={lp("/")} className="flex items-center gap-2">
            <HirafiIcon size={36} />
            <span className="font-display text-xl font-bold text-foreground">Hirafi</span>
          </Link>
          <Link to={lp("/")}>
            <Button variant="ghost" size="sm">
              <DirectionalArrow direction="left" className="me-1 h-4 w-4" /> {t("espace_artisan.back_home")}
            </Button>
          </Link>
        </div>
      </nav>

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img src={heroImage} alt="Artisan au travail" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground/90 via-foreground/75 to-foreground/40" />
          </div>
          <div className="container relative z-10 py-20 md:py-28">
            <div className="max-w-2xl space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm text-primary-foreground backdrop-blur-sm">
                <Wrench className="h-4 w-4" />
                <span>{t("espace_artisan.pro_badge")}</span>
              </div>
              <h1 className="font-display text-4xl font-bold leading-tight text-background md:text-5xl">
                {t("espace_artisan.hero_title")}
              </h1>
              <p className="text-lg text-background/80 md:text-xl">
                {t("espace_artisan.hero_subtitle")}
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                {user ? (
                  <Link to={lp("/dashboard")}>
                    <Button variant="hero" size="xl" className="w-full sm:w-auto">
                      {t("espace_artisan.go_dashboard")}
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to={lp("/artisan/login")}>
                      <Button variant="hero" size="xl" className="w-full sm:w-auto">
                        <LogIn className="me-2 h-5 w-5" />
                        {t("espace_artisan.login")}
                      </Button>
                    </Link>
                    <Link to={lp("/inscription")}>
                      <Button size="xl" className="w-full bg-background/15 text-background border border-background/30 hover:bg-background/25 backdrop-blur-sm sm:w-auto">
                        <UserPlus className="me-2 h-5 w-5" />
                        {t("espace_artisan.register")}
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Value Props */}
        <section className="container py-16">
          <div className="grid gap-6 md:grid-cols-3">
            {valueProps.map((vp) => (
              <Card key={vp.title} className="text-center">
                <CardContent className="p-6 space-y-3">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10">
                    <vp.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-display text-lg font-semibold">{vp.title}</h3>
                  <p className="text-sm text-muted-foreground">{vp.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Public Annonces */}
        <section className="container pb-16">
          <div className="mb-8">
            <h2 className="font-display text-2xl font-bold">{t("espace_artisan.annonces_title")}</h2>
            <p className="mt-1 text-muted-foreground">{t("espace_artisan.annonces_subtitle")}</p>
          </div>

          {isLoading ? (
            <p className="text-center text-muted-foreground py-12">{t("espace_artisan.loading")}</p>
          ) : annonces.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">{t("espace_artisan.no_annonces")}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {annonces.slice(0, 6).map((annonce) => (
                <Card key={annonce.id} className="group overflow-hidden transition-all hover:shadow-lg hover:border-primary/20">
                  <CardContent className="p-5">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          <Wrench className="me-1 h-3 w-3" />
                          {t(`annonces:categories_all.${annonce.category}`, { defaultValue: annonce.category || annonce.metier || "" })}
                        </Badge>
                        {annonce.urgency === "asap" && (
                          <Badge className="bg-destructive text-destructive-foreground text-xs">
                            <Zap className="me-1 h-3 w-3" />{t("espace_artisan.urgent")}
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-display text-lg font-semibold leading-tight">{annonce.title}</h3>
                      {annonce.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{annonce.description}</p>
                      )}
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>{annonce.commune}, {annonce.wilaya}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{t(`annonces:urgency.${annonce.urgency}`, { defaultValue: annonce.urgency || "" })}</span>
                      </div>
                      {(annonce.budget_min || annonce.budget_max) && (
                        <div className="flex items-center gap-1 font-medium text-foreground">
                          <span className="font-bold text-[10px] leading-none">DA</span>
                          <span>{getBudgetLabel(annonce)}</span>
                        </div>
                      )}
                      {annonce.photos && annonce.photos.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Camera className="h-3.5 w-3.5" />
                          <span>{t("espace_artisan.photo_count", { count: annonce.photos.length })}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 flex items-center justify-between border-t pt-3">
                      <span className="text-xs text-muted-foreground">
                        {new Date(annonce.created_at).toLocaleDateString(dateLocale, { day: "numeric", month: "short" })}
                      </span>
                      <div className="flex items-center gap-2">
                        {!user && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5 text-muted-foreground"
                            onClick={() => setShowAuthModal(true)}
                          >
                            <Lock className="h-3.5 w-3.5" />
                            {t("espace_artisan.login_to_contact")}
                          </Button>
                        )}
                        <Link to={lp(`/annonce/${annonce.id}`)}>
                          <Button variant="ghost" size="sm">
                            {t("espace_artisan.see_details")} <DirectionalArrow className="ms-1 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {annonces.length > 6 && (
            <div className="mt-6 text-center">
              <Link to={lp("/annonces")}>
                <Button variant="outline" size="lg">
                  {t("espace_artisan.see_all")} <DirectionalArrow className="ms-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          )}
        </section>

        {/* CTA bottom */}
        {!user && (
          <section className="gradient-hero py-16">
            <div className="container text-center space-y-4">
              <h2 className="font-display text-3xl font-bold text-primary-foreground">
                {t("espace_artisan.cta_title")}
              </h2>
              <p className="text-primary-foreground/80 max-w-xl mx-auto">
                {t("espace_artisan.cta_subtitle")}
              </p>
              <div className="flex justify-center gap-3">
                <Link to={lp("/inscription")}>
                  <Button size="xl" className="bg-background text-foreground hover:bg-background/90">
                    {t("espace_artisan.cta_btn")}
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{t("espace_artisan.auth_modal_title")}</DialogTitle>
            <DialogDescription>
              {t("espace_artisan.auth_modal_desc")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-2">
            <Link to={lp("/artisan/login")} onClick={() => setShowAuthModal(false)}>
              <Button variant="hero" className="w-full" size="lg">
                <LogIn className="me-2 h-5 w-5" />
                {t("espace_artisan.auth_modal_login")}
              </Button>
            </Link>
            <Link to={lp("/inscription")} onClick={() => setShowAuthModal(false)}>
              <Button variant="outline" className="w-full" size="lg">
                <UserPlus className="me-2 h-5 w-5" />
                {t("espace_artisan.auth_modal_register")}
              </Button>
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EspaceArtisan;
